import React, { useState, useEffect } from 'react'
import cn from 'classnames'
import { useDispatch, useSelector } from 'react-redux'
import Modal from '../../Modal'
import CardWrapper from '../../CardWrapper/CardWrapper'
import AuthCode from "react-auth-code-input"
import Loader from '../../Loader'
import { generateQRCodeStart, verify2FACodeStart } from '../../../store/general/auth/twoFactorAuth/actions'
import { toggleCurrentModal } from '../../../store/actions'
import styles from '../TwoFactorAuth.module.sass'

export function EnableTwoFactorAuthModal(props) {
  const { clientData } = useSelector((state) => state?.Profile)
  const [twoFACode, setTwoFACode] = useState('');
  const [showVerification, setShowVerification] = useState(false);
  const { verifyCode, generateQR } = useSelector(state => state.twoFactorAuthReducer);
  const { currentModal } = useSelector((state) => ({
    currentModal: state.Layout.currentModal,
  }));
  const dispatch = useDispatch()
  const focusInput2FA = (digits) => {
    const activeInputs = document.querySelectorAll(".twofa-input.active");
    const inputs = document.querySelectorAll(".twofa-input");
    if (activeInputs.length > digits.toString().length)
      activeInputs[activeInputs.length - 1]?.classList.remove("active");
    else
      inputs[digits.toString().length - 1]?.classList.add("active");
    setTwoFACode(digits);
    if (digits.length == 6) enableTwoFactorAuth(digits);
  };

  const enableTwoFactorAuth = (digits) => {
    if (digits || twoFACode?.length === 6) {
      props.onSubmit ? props.onSubmit(digits || twoFACode) : defaultVerify(digits || twoFACode)
    }
  };

  const verify = (code) => {
    enableTwoFactorAuth(code);
  };

  const defaultVerify = async (code) => {
    if ((code || twoFACode)?.length === 6) {

      dispatch(verify2FACodeStart({
        token: code || twoFACode,
        email: clientData && clientData.email && clientData.email.toLowerCase(),
        type: 'enable',
      }));
    }
  };

  // Generate QR code when modal opens
  useEffect(() => {
    if (currentModal === (props.currentModal || "enableTwoFactorAuthModal") && !generateQR.qrCode) {
      dispatch(generateQRCodeStart());
    }
  }, [currentModal, dispatch, generateQR.qrCode]);

  // Show verification input when QR code is generated
  useEffect(() => {
    if (generateQR && generateQR.qrCode) {
      setShowVerification(true);
    } else {
      setShowVerification(false);
    }
  }, [generateQR.qrCode]);

  useEffect(() => {
    if (verifyCode?.result?.type === "enable" && verifyCode.success) {
      // Close modal on successful verification
      dispatch(toggleCurrentModal(""));
      if (props.onClose) props.onClose();
      if (props.onSuccess) props.onSuccess();
    }
  }, [verifyCode, dispatch, props]);

  function detectDevice() {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;

    if (/android/i.test(userAgent)) {
      return 'android';
    } else if (/iPad|iPhone|iPod/i.test(userAgent)) {
      return 'ios';
    } else {
      return 'other';
    }
  }

  function openApp() {
    const device = detectDevice();
    let appLink;

    if (device === 'android') {
      appLink = 'https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2&hl=en&gl=US';
    } else if (device === 'ios') {
      appLink = 'https://apps.apple.com/us/app/google-authenticator/id388497605';
    } else {
      appLink = 'https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2&hl=en&gl=US';
    }

    window.open(appLink, '_blank');
  }

  return (
    <Modal
      visible={currentModal === (props.currentModal || "enableTwoFactorAuthModal")}
      onClose={() => {
        dispatch(toggleCurrentModal(""))
      }}
      outerClassName={styles.modal}
      isClosable={props.isClosable}
    >
      <div className="modal-header">
        <h5 className="modal-title mt-0">{props.t ? props.t("Enable Two Factor Authentication") : "Enable Two Factor Authentication"}</h5>
      </div>
      <div className="modal-body">
        {/* QR Code Section */}
        {generateQR?.qrCode && (
          <CardWrapper className="mb-3">
            <div className={styles.qrCodeWrapper}>
              <div className="mb-4 text-center">
                <div className={styles.code}>
                  {generateQR.loading ? <Loader /> :
                    <img src={generateQR.qrCode} alt="qr-code" width={200} height={200} />}
                </div>
              </div>
              <div>
                <div className={styles.btns}>
                  <p className={styles.text}>
                    {props.t ? props.t("If you want to turn on 2FA, use Google Authenticator app to scan code, then enter six-digit code provided by the app to the form below.") :
                      "If you want to turn on 2FA, use Google Authenticator app to scan code, then enter six-digit code provided by the app to the form below."}
                  </p>
                  <button onClick={openApp} className={cn('button-stroke', styles.button)}>
                    {props.t ? props.t("Download 2FA App") : "Download 2FA App"}
                  </button>
                </div>
              </div>
            </div>
          </CardWrapper>
        )}

        {/* Verification Section */}
        {showVerification && (
          <>
            <h1 className="mb-3">{props.t ? props.t("Enter Six-Digit Code") : "Enter Six-Digit Code"}</h1>
            <CardWrapper>
              <div>
                <AuthCode
                  characters={6}
                  className="form-control form-control-lg text-center w-100"
                  allowedCharacters="numeric"
                  containerClassName="twofa-container"
                  inputClassName="twofa-input"
                  onChange={(a) => { focusInput2FA(a) }}
                />
              </div>
            </CardWrapper>
            <div className="text-center mt-3">
              <div className={styles.btns}>
                <button
                  className={cn('button-red', styles.button)}
                  onClick={() => props.onSubmit ? props.onSubmit(twoFACode) : verify(twoFACode)}
                  disabled={(verifyCode.loading || twoFACode?.length < 6)}
                >
                  {verifyCode.loading ? "Verifying..." : (props.t ? props.t("Verify") : "Verify")}
                </button>
              </div>
            </div>
          </>
        )}

        {/* Loading state when generating QR */}
        {generateQR.loading && !generateQR.qrCode && (
          <div className="text-center">
            <Loader />
            <p className="mt-2">{props.t ? props.t("Generating QR Code...") : "Generating QR Code..."}</p>
          </div>
        )}
      </div>
    </Modal>
  );
}